<template>
  <div
    v-if="loading"
    class="module-rules-form-changes__loading"
  >
    <UIPreloader message="Loading changes..." />
  </div>
  <template v-else-if="isNothingToChange">
    <div class="module-rules__no-data">
      <UILayoutNoData
        inline
        hide-question-icon
      >
        <template #text-description>
          Nothing to change
        </template>
      </UILayoutNoData>
    </div>
    <div class="module-rules__buttons">
      <slot name="before" />
      <slot name="after" />
    </div>
  </template>
  <template v-else>
    <div
      v-for="(list, date) in scope"
      :key="date"
      class="module-rules-form-changes"
    >
      <div class="module-rules-form-changes__date">
        {{ date }}
      </div>
      <slot
        name="change"
        :actions
        :list
      />
    </div>
    <div class="module-rules__buttons">
      <slot name="before" />
      <UIPagination
        :page-no
        :disabled="loading"
        :page-size="pageSize"
        :total="pagination?.total"
        class="module-rules-form-changes__pagination"
        @click:page="handleClickPage"
        @update:page-size="handleUpdatePageSize"
      />
      <slot name="after" />
    </div>
  </template>
</template>

<script setup lang="ts" generic="T">
import { computed, nextTick, onBeforeMount, onWatcherCleanup, ref, watch } from 'vue'
import { groupBy, orderBy } from 'lodash'
import { watchPausable } from '@vueuse/core'

import {
  LinkedDataTransaction,
  PaginatedMeta,
  PaginatedResponse,
  TransactionRule,
} from '@types'

import { PAGE_SIZE } from '@/const/common'

import { UILayoutNoData, UIPreloader, UIPagination } from '@ui/core'

type Props = {
  data?: TransactionRule<T>['data']
}

type Emits = {
  fetch: [
    data: TransactionRule<T>['data'],
    pageNo: number,
    pageSize: number,
    callback: (data: PaginatedResponse<LinkedDataTransaction>) => void,
    signal?: AbortSignal,
  ]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const transactions = ref<LinkedDataTransaction[]>()
const pagination = ref<PaginatedMeta>()

const loading = ref(false)

const pageNo = ref(0)
const pageSize = ref(PAGE_SIZE)

const scope = computed(() =>
  groupBy(orderBy(transactions.value, 'date', 'desc'), 'date'),
)

const isNothingToChange = computed(() => !Object.keys(scope.value).length)

const actions = computed(() => props.data?.actions || [])

const fetch = async (signal?: AbortSignal) => {
  if (!props.data) return
  loading.value = true
  emit(
    'fetch',
    props.data,
    pageNo.value,
    pageSize.value,
    data => {
      transactions.value = data.data
      pagination.value = data.meta
      loading.value = false
    },
    signal,
  )
}

const handleClickPage = (page: number) => {
  pageNo.value = page
}

const handleUpdatePageSize = (newPageSize: number) => {
  if (newPageSize === pageSize.value) return

  pausePageNo()
  pageNo.value = 0
  nextTick(resumePageNo)
  pageSize.value = newPageSize
}

const { pause: pausePageNo, resume: resumePageNo } = watchPausable(
  pageNo,
  () => {
    const controller = new AbortController()
    fetch(controller.signal)
    onWatcherCleanup(() => {
      controller.abort()
    })
  },
)

watch(pageSize, () => {
  const controller = new AbortController()
  fetch(controller.signal)
  onWatcherCleanup(() => {
    controller.abort()
  })
})

onBeforeMount(fetch)
</script>

<style>
.module-rules-form-changes {
  @apply mb-2;

  &__loading {
    @apply h-[15rem];
    @apply flex items-center justify-center;
  }

  &__date {
    @apply sticky top-0;
    @apply mb-2;
    @apply py-2 px-4 md:px-6;
    @apply bg-gray-100 dark:bg-gray-800;
    @apply text-sm sm:text-xs;
  }

  &__item,
  &__date {
    @apply py-2 px-4 md:px-6;
  }

  &__item {
    @apply border-b border-gray-100 dark:border-gray-900;
    @apply last:border-none;
  }

  &__pagination {
    @apply !py-0 sm:!py-4 sm:px-4 md:px-6;
  }
}
</style>
